import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ParkCard from "@/components/ParkCard";
import CompareDrawer from "@/components/CompareDrawer";
import { ParkCardSkeletonGrid } from "@/components/Skeletons";
import useSeo from "@/lib/seo";
import { api } from "@/lib/api";
import { findLanding, LANDING_PAGES } from "@/lib/content";
import { ChevronDown, ArrowRight } from "lucide-react";

export default function Landing() {
  const { slug } = useParams();
  const def = findLanding(slug);
  const [results, setResults] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useSeo({
    title: def?.metaTitle,
    description: def?.description,
    keywords: def?.keywords,
    canonical: typeof window !== "undefined" ? `${window.location.origin}/collections/${slug}` : undefined,
  });

  useEffect(() => {
    if (!def) return;
    setResults(null);
    const params = { ...def.filters, limit: def.limit || 18 };
    api.searchParks(params).then((d) => setResults(d.results));
  }, [slug, def]);

  if (!def) return <Navigate to="/guides" replace />;

  const otherLandings = LANDING_PAGES.filter((l) => l.slug !== slug);

  return (
    <div className="App">
      <Nav />

      <header className="text-white" style={{ background: `linear-gradient(135deg, ${def.accent} 0%, ${shade(def.accent, -25)} 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link to="/guides" className="text-sm text-white/80 hover:text-white">← All guides &amp; collections</Link>
          <span className="inline-block mt-4 bg-white/20 backdrop-blur text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">{def.badge}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-3 leading-tight tracking-tight" data-testid="landing-title">{def.h1}</h1>
          <p className="text-white/90 mt-4 max-w-3xl text-lg leading-relaxed">{def.intro}</p>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-sky/10 border border-sky/30 rounded-xl px-4 py-3 mb-6 text-sm text-ink/80">
          <strong className="text-ink">Prices shown are indicative.</strong> Live availability and final pricing are confirmed on the operator's own site.
        </div>

        {!results ? <ParkCardSkeletonGrid count={9} /> : results.length === 0 ? (
          <div className="text-center py-20 bg-white border border-border rounded-2xl">
            <h3 className="font-display text-xl font-semibold">No matching parks just now</h3>
            <Link to="/search" className="btn-primary mt-5 inline-flex">Browse all parks</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="landing-results-grid">
            {results.map((p) => <ParkCard key={p.slug} park={p} />)}
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-display font-bold text-3xl mb-6">Frequently asked</h2>
        <div className="space-y-3" data-testid="landing-faqs">
          {def.faqs.map((faq, i) => (
            <button
              key={i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              className="w-full text-left bg-white border border-border rounded-xl p-5 hover:border-forest transition-colors"
              data-testid={`landing-faq-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-display font-semibold text-lg">{faq.q}</span>
                <ChevronDown className={`w-5 h-5 text-forest shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </div>
              {openFaq === i && <p className="text-ink/75 mt-3 leading-relaxed">{faq.a}</p>}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-display font-bold text-2xl mb-5">More UK caravan collections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {otherLandings.map((l) => (
            <Link key={l.slug} to={`/collections/${l.slug}`} className="bg-white border border-border rounded-2xl p-5 hover:border-forest hover:shadow-md transition-all">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: l.accent }}>{l.badge}</span>
              <h3 className="font-display font-bold text-lg mt-2">{l.h1}</h3>
              <span className="inline-flex items-center gap-1 text-sm text-forest mt-3">Open collection <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </section>

      <CompareDrawer />
      <Footer />
    </div>
  );
}

function shade(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0xff) + percent;
  let b = (num & 0xff) + percent;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}
